/// <reference types="google.maps" />
/**
 * Loads the Google Maps JS API exactly once for the whole app via a single
 * injected `<script>` tag (no heavy React-Google-Maps wrapper lib).
 *
 * The `places` + `geometry` libraries are requested up front so the Places page
 * can run `PlacesService.nearbySearch` and `spherical.computeDistanceBetween`
 * without an extra `importLibrary` round-trip.
 *
 * Failure is a first-class outcome, NOT a throw: the key may be HTTP-referrer
 * restricted to yaarikut.com (so it won't authorise on localhost) or blank in
 * some envs. Callers branch on `status` and render a friendly fallback rather
 * than crashing.
 */
import { useEffect, useState } from 'react';

/** Loading lifecycle of the Maps JS API. */
export type MapsStatus = 'idle' | 'loading' | 'ready' | 'error' | 'no-key';

const SCRIPT_ID = 'gmaps-js-api';

/**
 * Module-level singleton promise so concurrent callers (page + autocomplete)
 * share one script load instead of each injecting their own tag.
 */
let loaderPromise: Promise<void> | null = null;

/** Build the loader URL for the given key (places + geometry, English). */
export function mapsScriptSrc(key: string): string {
  return `https://maps.googleapis.com/maps/api/js?libraries=places,geometry&language=en&key=${key}`;
}

function loadMaps(key: string): Promise<void> {
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise<void>((resolve, reject) => {
    // Already present (e.g. HMR re-mount) — resolve immediately.
    if (typeof window !== 'undefined' && window.google?.maps) {
      resolve();
      return;
    }

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing ?? document.createElement('script');

    const onLoad = () => {
      // Google can "load" the script but still fail auth (bad/referrer-locked
      // key) — in that case `window.google.maps` never materialises.
      if (window.google?.maps) resolve();
      else reject(new Error('google.maps unavailable after load'));
    };
    const onError = () => reject(new Error('Failed to load Google Maps JS API'));

    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });

    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = mapsScriptSrc(key);
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  // A rejected singleton must not poison future mounts — clear it so a later
  // retry (e.g. after the user reloads) can attempt again.
  loaderPromise.catch(() => {
    loaderPromise = null;
  });

  return loaderPromise;
}

interface UseGoogleMapsOptions {
  /**
   * When `false`, return `'idle'` without ever calling `loadMaps`.
   * Lets non-critical surfaces (e.g. the dashboard right-rail "Who's
   * near you" widget) skip the ~120 kB script load entirely until
   * the user actually needs it. Defaults to `true` so the Places /
   * Register / EditProfile pages keep their immediate-load behavior.
   */
  enabled?: boolean;
}

/**
 * React hook wrapping the singleton loader. Returns the current `status`; when
 * `ready`, `window.google.maps` is guaranteed available.
 */
export function useGoogleMaps(options: UseGoogleMapsOptions = {}): { status: MapsStatus } {
  const { enabled = true } = options;
  const key = import.meta.env.VITE_GOOGLE_MAPS_KEY || '';
  // Derive the synchronous initial status so the effect only ever calls
  // setStatus from async callbacks (avoids cascading-render lint / re-renders).
  const initial: MapsStatus = !enabled
    ? 'idle'
    : !key
      ? 'no-key'
      : window.google?.maps
        ? 'ready'
        : 'loading';
  const [status, setStatus] = useState<MapsStatus>(initial);

  useEffect(() => {
    // When `enabled` flips false → true, `initial` recomputes but
    // `useState(initial)` is only evaluated on first render — without
    // an explicit sync, `status` stays on whatever was set on mount
    // (commonly `'idle'`). Reflect every transition that does NOT
    // require an async fetch.
    if (initial === 'idle' || initial === 'no-key' || initial === 'ready') {
      if (status !== initial) setStatus(initial);
      return;
    }
    // initial === 'loading' → script not present, drive a load.
    let active = true;
    loadMaps(key)
      .then(() => {
        if (active) setStatus('ready');
      })
      .catch(() => {
        if (active) setStatus('error');
      });

    return () => {
      active = false;
    };
  }, [key, initial, status]);

  return { status };
}

/** Test-only: reset the module singleton between specs. */
export function __resetMapsLoader(): void {
  loaderPromise = null;
}
